import cn from 'classnames';
import * as React from 'react';

import { hasDisplayName } from 'utils/hasDisplayName';

import s from './Spacing.module.scss';
import {
  SpacingRow,
  SpacingRowItem,
  SpacingRowProps,
  SpacingRowItemProps,
} from './SpacingRow';
import { SpacingRowBase, SpacingRowBaseProps } from './SpacingRowBase';
import { SpacingUnit } from './config';
import { SpacingCtx, usePassThroughSpacing } from './context';

export type SpacingProps = React.HTMLProps<HTMLDivElement> & {
  spacing?: SpacingUnit;
  children?: React.ReactNode | React.ReactNode[];
  flex?: 'column' | 'row';
};

const Spacing: React.FC<SpacingProps> & {
  /**
   * Без разделения на элементы строки
   */
  RowBase: React.ComponentType<SpacingRowBaseProps>;

  /**
   * С разделением на элементы строки
   */
  Row: React.ComponentType<SpacingRowProps>;

  /**
   * Элемент строки
   */
  RowItem: React.ComponentType<SpacingRowItemProps>;
} = ({
  className,
  children,
  spacing = SpacingUnit.unit2,
  flex,
  ...props
}: SpacingProps) => {
  const filteredChildren = React.useMemo(() => {
    return React.Children.toArray(children).filter(Boolean);
  }, [children]);

  const nextValue = usePassThroughSpacing({
    vSpacing: spacing,
    betweenRows: spacing,
  });

  return (
    <SpacingCtx.Provider value={nextValue}>
      <div
        className={cn(
          s['spacing'],
          flex && s['spacing_flex'],
          flex === 'column' && s['spacing_flex-column'],
          className
        )}
        {...props}
      >
        {React.Children.map(filteredChildren, (child) =>
          hasDisplayName(child) &&
          (child.type.displayName === SpacingRowBase.displayName ||
            child.type.displayName === SpacingRow.displayName) ? (
            child
          ) : (
            <SpacingRowBase>{child}</SpacingRowBase>
          )
        )}
      </div>
    </SpacingCtx.Provider>
  );
};

Spacing.RowBase = SpacingRowBase;
Spacing.Row = SpacingRow;
Spacing.RowItem = SpacingRowItem;

export default Spacing;
